const MIN_SIDE = 64;
const MAX_SIDE = 8192;
const SIZE_STEP = 16;
/** OpenRouter: максимум 3:1 (и зеркально 1:3) */
export const MAX_ASPECT = 3;

/** Округление стороны до кратного 16 (требование OpenRouter) */
export function snapToStep(value, step = SIZE_STEP) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return MIN_SIDE;
    const snapped = Math.round(n / step) * step;
    return Math.max(MIN_SIDE, Math.min(MAX_SIDE, snapped || step));
}

/**
 * Нормализует WxH: кратность 16 + ограничение aspect ≤ 3:1.
 * При нарушении сохраняем большую сторону, подтягиваем меньшую.
 */
export function normalizePixelSize(width, height) {
    let w = snapToStep(width);
    let h = snapToStep(height);

    if (h > w * MAX_ASPECT) {
        // портрет слишком узкий — расширяем ширину
        w = snapToStep(h / MAX_ASPECT);
        // floor-snap если всё ещё выходим за 3:1 из‑за округления вверх
        if (h > w * MAX_ASPECT) {
            w = Math.min(MAX_SIDE, Math.ceil(h / MAX_ASPECT / SIZE_STEP) * SIZE_STEP);
        }
    } else if (w > h * MAX_ASPECT) {
        h = snapToStep(w / MAX_ASPECT);
        if (w > h * MAX_ASPECT) {
            h = Math.min(MAX_SIDE, Math.ceil(w / MAX_ASPECT / SIZE_STEP) * SIZE_STEP);
        }
    }

    w = Math.max(MIN_SIDE, Math.min(MAX_SIDE, w));
    h = Math.max(MIN_SIDE, Math.min(MAX_SIDE, h));
    return { width: w, height: h };
}

/** "16:9" → { w, h } */
export function parseRatio(ratio) {
    const parts = String(ratio || '1:1')
        .split(':')
        .map((x) => Number(String(x).trim()));
    if (parts.length !== 2 || !parts[0] || !parts[1]) return { w: 1, h: 1 };
    return { w: parts[0], h: parts[1] };
}

/** Размер по длинной стороне и aspect ratio (кратность 16 + max 3:1) */
export function sizeFromAspect(ratio, longEdge = 1024) {
    const edge = snapToStep(longEdge);
    let { w, h } = parseRatio(ratio);
    if (w > h * MAX_ASPECT) w = h * MAX_ASPECT;
    if (h > w * MAX_ASPECT) h = w * MAX_ASPECT;

    if (w >= h) {
        return normalizePixelSize(edge, (edge * h) / w);
    }
    return normalizePixelSize((edge * w) / h, edge);
}

export const RESOLUTION_TIERS = ['512', '1K', '2K', '4K'];

const NAMED_RESOLUTION_TIERS = {
    '512': '512',
    '1k': '1K',
    '1.5k': '1K',
    '2k': '2K',
    '4k': '4K',
    '8k': '4K',
    '720p': '1K',
    '1080p': '2K',
    '1mp': '1K',
    '4mp': '2K',
};

export function longEdgeFromTier(tier) {
    const key = String(tier || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
    const map = { '512': 512, '1k': 1024, '2k': 2048, '4k': 4096 };
    return map[key] || 1024;
}

export function tierFromLongEdge(longEdge) {
    const edge = Number(longEdge);
    if (!Number.isFinite(edge) || edge < 64) return null;
    if (edge >= 3200) return '4K';
    if (edge >= 1600) return '2K';
    if (edge >= 768) return '1K';
    return '512';
}

/** Сводит 1k / 1024x1024 / 1080p к 512 · 1K · 2K · 4K; мусор (1:1, auto, 8) отбрасывает. */
export function normalizeResolutionTier(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const key = raw
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/×/g, 'x')
        .replace(/\*/g, 'x');
    if (NAMED_RESOLUTION_TIERS[key]) return NAMED_RESOLUTION_TIERS[key];
    if (/^\d+(\.\d+)?:\d+(\.\d+)?$/.test(key)) return null;
    const px = key.match(/^(\d+)x(\d+)$/);
    if (px) {
        return tierFromLongEdge(Math.max(Number(px[1]), Number(px[2])));
    }
    return null;
}

function resolutionListFromParam(res) {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (typeof res === 'string' || typeof res === 'number') return [res];
    if (typeof res === 'object') {
        if (Array.isArray(res.values)) return res.values;
        if (Array.isArray(res.enum)) return res.enum;
        if (Array.isArray(res.options)) return res.options;
    }
    return [];
}

/** Канонические resolution-tiers из supported_parameters image-модели */
export function getModelResolutionValues(params) {
    const raw = [
        ...resolutionListFromParam(params?.resolution),
        ...resolutionListFromParam(params?.resolutions),
    ];
    const values = [];
    const seen = new Set();
    for (const item of raw) {
        const tier = normalizeResolutionTier(item);
        if (!tier || seen.has(tier)) continue;
        seen.add(tier);
        values.push(tier);
    }
    return values;
}

export function modelSupportsResolution(params, filter) {
    const want = normalizeResolutionTier(filter);
    if (!want) return false;
    const vals = getModelResolutionValues(params);
    if (!vals.length) return true;
    return vals.includes(want);
}

/** Максимум n (сколько изображений за раз) из supported_parameters */
export function getModelNMax(params) {
    const n = params?.n;
    if (n && typeof n === 'object') {
        const max = Number(n.max);
        if (Number.isFinite(max) && max >= 1) return Math.floor(max);
    }
    for (const key of ['max_images', 'max_output_images']) {
        const max = Number(params?.[key]);
        if (Number.isFinite(max) && max >= 1) return Math.floor(max);
    }
    return 1;
}


export function formatSize(width, height) {
    const { width: w, height: h } = normalizePixelSize(width, height);
    if (!w || !h) return null;
    return `${w}x${h}`;
}

function gcd(a, b) {
    let x = Math.abs(Math.round(Number(a)) || 0);
    let y = Math.abs(Math.round(Number(b)) || 0);
    while (y) {
        const t = y;
        y = x % y;
        x = t;
    }
    return x || 1;
}

/** 1920×1080 → "16:9" */
export function simplifyRatio(width, height) {
    const w = Math.round(Number(width));
    const h = Math.round(Number(height));
    if (!w || !h) return null;
    const g = gcd(w, h);
    return `${w / g}:${h / g}`;
}

/** "1024x768" / "1024×768" → { width, height } */
export function parseSizeString(size) {
    const text = String(size || '')
        .trim()
        .toLowerCase()
        .replace('×', 'x');
    if (!text.includes('x')) return null;
    const [left, right] = text.split('x');
    const width = Number(left?.trim());
    const height = Number(right?.trim());
    if (!width || !height) return null;
    return { width, height };
}

export function exceedsMaxAspect(width, height) {
    const w = Number(width);
    const h = Number(height);
    if (!w || !h) return false;
    return w > h * MAX_ASPECT || h > w * MAX_ASPECT;
}
